import {
	Area,
	CartesianGrid,
	ComposedChart,
	Line,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import type { EquityCurvePoint } from "./chart-data";
import { CHART_COLORS, CHART_FONT, CSS_VARS, useChartTheme } from "./chart-theme";

interface Props {
	data: EquityCurvePoint[];
}

const CustomTooltip = ({
	active,
	payload,
}: {
	active?: boolean;
	payload?: Array<{ payload: EquityCurvePoint }>;
}) => {
	if (!active || !payload?.[0]) return null;
	const p = payload[0].payload;
	return (
		<div
			className="rounded-md border px-2.5 py-1.5 text-[0.72rem] shadow-lg"
			style={{
				background: CSS_VARS.tooltip,
				borderColor: CSS_VARS.tooltipBorder,
			}}
		>
			<p className="tg-text-muted">{p.label}</p>
			<p className="font-medium" style={{ color: CHART_COLORS.blue }}>
				{p.pair}
			</p>
			<p
				className="font-semibold"
				style={{ color: p.tradePL >= 0 ? CHART_COLORS.green : CHART_COLORS.red }}
			>
				Trade: {p.tradePL >= 0 ? "+" : ""}
				{p.tradePL.toFixed(2)}
			</p>
			<p className="tg-text-primary font-semibold">
				Cumulative: {p.cumulativePL >= 0 ? "+" : ""}
				{p.cumulativePL.toFixed(2)}
			</p>
			{p.drawdown < 0 ? (
				<p style={{ color: CHART_COLORS.red }}>
					Drawdown: {p.drawdown.toFixed(2)}
				</p>
			) : null}
		</div>
	);
};

export const EquityCurveChart = ({ data }: Props) => {
	const theme = useChartTheme();

	if (data.length < 2) {
		return (
			<p className="flex h-full items-center justify-center text-[0.78rem] tg-text-muted">
				Need at least 2 closed trades to show the equity curve.
			</p>
		);
	}

	return (
		<ResponsiveContainer width="100%" height="100%">
			<ComposedChart
				data={data}
				margin={{ top: 8, right: 12, bottom: 4, left: 0 }}
			>
				<CartesianGrid
					strokeDasharray="3 3"
					stroke={theme.grid}
					vertical={false}
				/>
				<XAxis
					dataKey="index"
					tick={{ fill: theme.axis, fontSize: CHART_FONT.size }}
					tickLine={false}
					axisLine={{ stroke: theme.grid }}
				/>
				<YAxis
					tick={{ fill: theme.axis, fontSize: CHART_FONT.size }}
					tickLine={false}
					axisLine={false}
					width={50}
					tickFormatter={(v: number) => `$${v}`}
				/>
				<Tooltip
					content={<CustomTooltip />}
					cursor={{ stroke: theme.muted, strokeDasharray: "3 3" }}
				/>
				<ReferenceLine y={0} stroke={theme.muted} strokeDasharray="3 3" />
				<Area
					type="monotone"
					dataKey="drawdown"
					fill={CHART_COLORS.red}
					fillOpacity={0.12}
					stroke="none"
					isAnimationActive={false}
				/>
				<Line
					type="monotone"
					dataKey="cumulativePL"
					stroke={CHART_COLORS.green}
					strokeWidth={2}
					dot={false}
					activeDot={{ r: 4, fill: CHART_COLORS.green }}
					isAnimationActive={false}
				/>
			</ComposedChart>
		</ResponsiveContainer>
	);
};
